import { useEffect, useState } from 'react';
import type { Project } from '@jabberr/core';
import { api } from './api.js';

export type TeamProject = Project & { role?: string };

export function meetingProjects(projects: TeamProject[]): TeamProject[] {
  return projects.filter(project => project.kind === 'team' && project.status === 'active' && !project.isIdea && (project.role === 'owner' || project.role === 'member'));
}

const recentKey = (userId: string) => `jabberr:recent-team-project:${userId}`;
export function recentTeamProject(userId: string): string | null {
  try { return localStorage.getItem(recentKey(userId)); }
  catch { return null; }
}
export function rememberTeamProject(userId: string, projectId: string): void {
  try { localStorage.setItem(recentKey(userId), projectId); }
  catch { /* Remote conversation activity remains a fallback. */ }
}

/** User activity wins over project edits; inaccessible or archived remembered projects are ignored. */
export function defaultMeetingProject(projects: TeamProject[], remembered: string | null, recentIds: string[] = []): TeamProject | null {
  const eligible = meetingProjects(projects);
  for (const id of [remembered, ...recentIds]) {
    const project = eligible.find(candidate => candidate.id === id);
    if (project) return project;
  }
  return [...eligible].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '') || a.name.localeCompare(b.name))[0] ?? null;
}

export function useMeetingProject(userId: string | undefined, projects: TeamProject[], requestedId?: string | null) {
  const [chosen, setChosen] = useState<{ userId: string; projectId: string } | null>(null);
  const [remote, setRemote] = useState<{ userId: string; ids: string[] } | null>(null);
  useEffect(() => {
    let alive = true;
    setChosen(null); setRemote(null);
    if (userId) void api.recentConversations(50).then(({ conversations }) => {
      if (alive) setRemote({ userId, ids: conversations.map(conversation => conversation.projectId) });
    }).catch(() => { if (alive) setRemote({ userId, ids: [] }); });
    return () => { alive = false; };
  }, [userId]);
  const eligible = meetingProjects(projects);
  const explicit = chosen && chosen.userId === userId ? chosen.projectId : requestedId;
  const remembered = userId ? recentTeamProject(userId) : null;
  const explicitProject = eligible.find(project => project.id === explicit);
  const rememberedProject = eligible.find(project => project.id === remembered);
  // An edited project is only a last resort. Do not show or remember it while the person's activity is still loading.
  const loading = eligible.length > 0 && !!userId && !explicitProject && !rememberedProject && remote?.userId !== userId;
  const selected = explicitProject ?? rememberedProject
    ?? (loading ? null : defaultMeetingProject(projects, null, remote && remote.userId === userId ? remote.ids : []));
  const choose = (projectId: string) => {
    if (!userId || !eligible.some(project => project.id === projectId)) return;
    rememberTeamProject(userId, projectId);
    setChosen({ userId, projectId });
  };
  return { projects: eligible, selected, choose, loading };
}
